import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowRight, Cloud, Brain, MapPin, TrendingUp, Shield,
  Zap, Globe, CheckCircle2, Building2, KeyRound, Landmark, Handshake, Calculator, DollarSign,
  Warehouse, Store, Building, Users2, Quote, BarChart3, Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useSEO from '../hooks/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }
  })
};

const HERO_SLIDES = [
  {
    img: 'https://images.unsplash.com/photo-1499566727020-881da110a0b0?w=1920&q=80&auto=format&fit=crop',
    city: 'New York',
    code: 'NY',
  },
  {
    img: 'https://images.unsplash.com/photo-1695776333029-d47e3f0f4e63?w=1920&q=80&auto=format&fit=crop',
    city: 'Miami',
    code: 'FL',
  },
  {
    img: 'https://images.unsplash.com/photo-1570940677726-10cb96370dba?w=1920&q=80&auto=format&fit=crop',
    city: 'Philadelphia',
    code: 'PA',
  },
];

export default function LandingPage() {
  const { login } = useAuth();
  const [dealSize, setDealSize] = useState(5000000);
  const [commRate, setCommRate] = useState(3);
  const [activeSlide, setActiveSlide] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  const [currentSplit, setCurrentSplit] = useState(70);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recruitLeaderboard, setRecruitLeaderboard] = useState([]);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % HERO_SLIDES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(() => {});
    axios.get(`${API}/public/stats`).then(r => setLiveStats(r.data)).catch(() => {});
    axios.get(`${API}/public/leaderboard`).then(r => setLeaderboard(r.data)).catch(() => {});
    axios.get(`${API}/public/recruit-leaderboard`).then(r => setRecruitLeaderboard(r.data)).catch(() => {});
  }, [API]);

  useSEO({
    title: 'Join Krieger Real Estate | Up to 90% Commission for Agents in NY, PA & FL',
    description: 'Looking for a new real estate broker? Krieger offers up to 90% commission with no monthly fees for residential and commercial agents in New York, Philadelphia, and Miami. Apply today.',
    keywords: 'looking for new real estate broker, switch real estate brokerage, best real estate commission split, 90 percent commission real estate, real estate agent jobs New York, real estate agent jobs Miami, real estate agent jobs Philadelphia, high commission real estate brokerage, no fee real estate brokerage, join new brokerage, change real estate broker, transfer real estate license, best brokerage for agents, residential real estate brokerage, commercial real estate brokerage',
    canonical: 'https://joinkrieger.com/',
  });

  const grossComm = dealSize * (commRate / 100);
  const krieger90 = grossComm * 0.9;
  const traditional70 = grossComm * 0.7;
  const traditional80 = grossComm * 0.8;
  const savings90vs70 = krieger90 - traditional70;

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="bg-[#FAFBFC] min-h-screen" data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end md:items-center overflow-hidden" data-testid="hero-section">
        {/* Carousel images */}
        <div className="absolute inset-0">
          {HERO_SLIDES.map((slide, i) => (
            <div
              key={slide.code}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{ opacity: activeSlide === i ? 1 : 0 }}
            >
              <img
                src={slide.img}
                alt={`${slide.city} skyline`}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-10 md:pb-16 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end md:items-center">
            {/* Text side */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
            >
              <motion.div variants={fadeUp} className="mb-5">
                <span className="text-sm sm:text-base tracking-wide uppercase text-[#002FA7] font-bold bg-[#EFF3FF] px-5 py-2.5 rounded-full inline-block border border-[#002FA7]/20">
                  Commercial & Residential Agents
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                custom={1}
                className="text-4xl sm:text-5xl lg:text-7xl font-['Cabinet_Grotesk'] font-bold tracking-tight leading-[0.95] mb-6 text-slate-900"
              >
                The Cloud-Based Brokerage<br />
                for <span className="text-[#002FA7]">Real Estate</span> Agents.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                custom={2}
                className="text-base md:text-lg text-slate-500 leading-relaxed max-w-lg mb-3"
              >
                Krieger Real Estate offers up to a 90% commission split with no monthly fees for qualified residential and commercial agents representing buyers, sellers, landlords, and tenants across New York, Pennsylvania, and Florida.
              </motion.p>
              <motion.p
                variants={fadeUp}
                custom={2}
                className="text-sm font-semibold text-[#002FA7] tracking-wide mb-8"
              >
                No desk fees. No monthly fees. No hidden charges. Ever.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-3">
                <Link
                  to="/apply"
                  className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-[#0040D6] transition-all shadow-lg shadow-blue-900/20 hover:shadow-xl hover:shadow-blue-900/25"
                  data-testid="hero-apply-btn"
                >
                  Apply to Join <ArrowRight size={16} />
                </Link>
                <button
                  onClick={login}
                  className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-7 py-3.5 rounded-full font-medium text-sm hover:bg-slate-50 transition-colors shadow-sm"
                  data-testid="hero-login-btn"
                >
                  Agent Portal <KeyRound size={16} />
                </button>
              </motion.div>

              {/* Inline stats + city indicator */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="mt-12 flex flex-wrap items-center gap-6"
              >
                {[
                  { value: 'Up to 90%', label: 'Commission' },
                  { value: '3', label: 'States' },
                  { value: '100%', label: 'Cloud-Based' },
                ].map((stat, i) => (
                  <div key={stat.label} className="flex items-center gap-3">
                    <div className="commission-highlight text-2xl md:text-3xl">{stat.value}</div>
                    <div className="text-[10px] tracking-[0.15em] uppercase text-slate-400 font-semibold leading-tight">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right side - floating card with city carousel indicator */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="hidden md:flex flex-col items-end gap-6"
            >
              {/* City indicator */}
              <div className="flex items-center gap-3 bg-white/70 backdrop-blur-xl border border-white/80 rounded-full px-5 py-2.5 shadow-lg" data-testid="city-indicator">
                <MapPin size={14} className="text-[#002FA7]" />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={activeSlide}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="text-sm font-['Cabinet_Grotesk'] font-medium text-slate-700"
                  >
                    {HERO_SLIDES[activeSlide].city}
                  </motion.span>
                </AnimatePresence>
                <div className="flex items-center gap-1.5 ml-2">
                  {HERO_SLIDES.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveSlide(i)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        activeSlide === i ? 'bg-[#002FA7] w-5' : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                      data-testid={`hero-dot-${i}`}
                      aria-label={`Show ${HERO_SLIDES[i].city}`}
                    />
                  ))}
                </div>
              </div>

              {/* Benefits card */}
              <div className="bg-white/70 backdrop-blur-xl border border-white/80 rounded-2xl p-8 shadow-2xl shadow-black/5 max-w-sm">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#002FA7] font-semibold mb-3">Why Agents Choose Krieger</div>
                <ul className="space-y-3">
                  {[
                    'Up to 90% commission for qualified agents',
                    'No desk fees or monthly fees',
                    'Residential sales, rentals & commercial',
                    'Cloud-based — work from anywhere',
                    'Statewide coverage in NY, PA, and FL',
                  ].map((item, i) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <CheckCircle2 size={16} className="text-[#002FA7] shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/apply"
                  className="mt-6 block text-center bg-[#002FA7] text-white py-2.5 rounded-full text-sm font-medium hover:bg-[#0040D6] transition-colors"
                >
                  Get Started Today
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMMISSION STRUCTURE */}
      <section id="commission" className="py-24 md:py-32 border-t border-slate-200" data-testid="commission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">
              Commission Structure
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
              How an Up-to-90% Commission Split Changes Your Income
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-xl mb-16">
              One flat commission split across all transaction types — residential sales, rentals, commercial sales, and leasing. No desk fees. No monthly fees. No technology charges. No complicated tiers. Qualified agents earn up to 90%.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Building2, title: 'Residential Sales', desc: 'Single-family homes, condos, co-ops, townhouses, and luxury properties' },
              { icon: Building2, title: 'Residential Rentals', desc: 'Apartments, houses, and luxury rentals — tenant and landlord representation' },
              { icon: Building2, title: 'Commercial Sales', desc: 'Multifamily, office, retail, self storage, and warehouse properties' },
              { icon: Handshake, title: 'Commercial Leasing', desc: 'Office, retail, restaurant, warehouse, and flex space leasing' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white border border-slate-200 p-8 md:p-10 rounded-xl group hover:shadow-lg hover:shadow-blue-900/5 transition-all"
              >
                <div className="w-12 h-12 bg-[#EFF3FF] rounded-xl flex items-center justify-center mb-6">
                  <item.icon size={22} className="text-[#002FA7]" />
                </div>
                <div className="commission-highlight text-5xl md:text-6xl mb-3">Up to 90%</div>
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium mb-2 text-slate-800">{item.title}</h3>
                <p className="text-sm text-slate-400 mb-3">{item.desc}</p>
                <div className="flex gap-2 flex-wrap">
                  <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">No Desk Fee</span>
                  <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">No Monthly Fee</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMERCIAL REAL ESTATE */}
      <section id="commercial" className="py-24 md:py-32 border-t border-slate-200 bg-[#F4F6F8]" data-testid="commercial-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">
              Property Types
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
              Residential & Commercial. Up to 90% Split.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-2xl mb-16">
              As a cloud-based, technology-oriented brokerage, we recruit residential and commercial agents across all property types — statewide in New York, Pennsylvania, and Florida.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Building2, title: 'Single-Family Homes', desc: 'Houses, townhouses, and luxury single-family properties — buyer and seller representation.', tag: 'Residential', img: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&q=80&auto=format&fit=crop' },
              { icon: Building2, title: 'Condos & Co-ops', desc: 'Condominiums, co-ops, and luxury high-rise residential units — sales and rentals.', tag: 'Residential', img: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80&auto=format&fit=crop' },
              { icon: Building2, title: 'Multifamily', desc: 'Apartment complexes, garden-style communities, mid-rise and high-rise investment properties.', tag: 'Commercial', img: 'https://images.unsplash.com/photo-1574362848149-11496d93a7c7?w=600&q=80&auto=format&fit=crop' },
              { icon: Landmark, title: 'Office Buildings', desc: 'Class A, B, and C office properties — sales and tenant/landlord representation.', tag: 'Commercial', img: 'https://images.unsplash.com/photo-1621831337128-35676ca30868?w=600&q=80&auto=format&fit=crop' },
              { icon: Store, title: 'Retail & Restaurant', desc: 'Shopping centers, strip malls, NNN retail, restaurants, and mixed-use developments.', tag: 'Commercial', img: 'https://images.pexels.com/photos/16140812/pexels-photo-16140812.jpeg?auto=compress&cs=tinysrgb&w=600' },
              { icon: Warehouse, title: 'Warehouse & Industrial', desc: 'Warehouses, distribution centers, flex space, manufacturing facilities, and logistics hubs.', tag: 'Commercial', img: 'https://images.unsplash.com/photo-1557761469-f29c6e201784?w=600&q=80&auto=format&fit=crop' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="bg-white border border-slate-200 p-0 rounded-xl group hover:shadow-lg hover:shadow-blue-900/5 transition-all relative overflow-hidden"
                data-testid={`commercial-card-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <img src={item.img} alt={item.title} className="w-full h-40 object-cover" />
                <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#EFF3FF] rounded-xl flex items-center justify-center">
                    <item.icon size={18} className="text-[#002FA7]" />
                  </div>
                  <span className="text-[9px] tracking-[0.15em] uppercase font-semibold text-[#002FA7] bg-[#EFF3FF] px-2.5 py-1 rounded-full">{item.tag}</span>
                </div>
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium mb-2 text-slate-800">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{item.desc}</p>
                <div className="flex items-center gap-2">
                  <span className="commission-highlight text-2xl">Up to 90%</span>
                  <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Commission</span>
                </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-12 bg-white border border-slate-200 rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            data-testid="commercial-cta"
          >
            <div>
              <h3 className="text-xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-2">Ready to keep up to 90% of what you earn?</h3>
              <p className="text-sm text-slate-500 max-w-lg">Join Krieger and leverage our platform, tools, and up-to-90% split on every residential and commercial transaction — statewide in NY, PA, and FL.</p>
            </div>
            <Link
              to="/apply"
              className="shrink-0 bg-[#002FA7] text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-[#0040D6] transition-colors flex items-center gap-2"
              data-testid="commercial-apply-btn"
            >
              Apply Now <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* COMMISSION CALCULATOR */}
      <section id="calculator" className="py-24 md:py-32 border-t border-slate-200 bg-[#F4F6F8]" data-testid="calculator-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">
              Commission Calculator
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
              Calculate Your Earnings Potential
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-xl mb-12">
              Input your typical deal size and see the difference an up-to-90% split makes compared to traditional brokerages.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Inputs */}
            <div className="bg-white border border-slate-200 p-8 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#EFF3FF] rounded-xl flex items-center justify-center">
                  <Calculator size={18} className="text-[#002FA7]" />
                </div>
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium text-slate-800">Your Deal</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold block mb-3">
                    Sale Price: {formatCurrency(dealSize)}
                  </label>
                  <input
                    type="range" min="1000000" max="50000000" step="500000" value={dealSize}
                    onChange={(e) => setDealSize(Number(e.target.value))}
                    className="w-full"
                    data-testid="calc-deal-slider"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                    <span>$1M</span><span>$50M</span>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold block mb-3">
                    Commission Rate: {commRate}%
                  </label>
                  <input
                    type="range" min="1" max="6" step="0.5" value={commRate}
                    onChange={(e) => setCommRate(Number(e.target.value))}
                    className="w-full"
                    data-testid="calc-rate-slider"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                    <span>1%</span><span>6%</span>
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-1">Gross Commission</div>
                  <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{formatCurrency(grossComm)}</div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <div className="bg-white border-2 border-[#002FA7]/20 p-6 rounded-xl shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign size={16} className="text-[#002FA7]" />
                    <span className="text-sm font-medium text-slate-800">Krieger 90/10</span>
                  </div>
                  <span className="text-[10px] bg-[#EFF3FF] text-[#002FA7] px-2 py-0.5 rounded-full font-semibold">YOU KEEP</span>
                </div>
                <div className="commission-highlight text-4xl mb-1" data-testid="calc-krieger-result">{formatCurrency(krieger90)}</div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-[#002FA7] rounded-full" style={{ width: '90%' }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-1">90% of {formatCurrency(grossComm)}</div>
              </div>
              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-500">Traditional 80/20</span>
                  <span className="text-[10px] text-slate-400">COMPETITOR</span>
                </div>
                <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-400">{formatCurrency(traditional80)}</div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-slate-300 rounded-full" style={{ width: '80%' }} />
                </div>
              </div>
              <div className="bg-white border border-slate-200 p-6 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-500">Traditional 70/30</span>
                  <span className="text-[10px] text-slate-400">COMPETITOR</span>
                </div>
                <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-400">{formatCurrency(traditional70)}</div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-slate-200 rounded-full" style={{ width: '70%' }} />
                </div>
              </div>
              <div className="bg-[#EFF3FF] border border-[#002FA7]/10 p-5 rounded-xl">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">Your Extra Earnings vs 70/30</div>
                <div className="text-xl font-['Cabinet_Grotesk'] font-bold text-emerald-600" data-testid="calc-savings">+{formatCurrency(savings90vs70)}</div>
                <div className="text-xs text-slate-400">per deal with Krieger</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="py-24 md:py-32 border-t border-slate-200" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">
              Why Krieger
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-16 text-slate-900">
              Why Agents Are Leaving Traditional Brokerages
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Brain, title: 'AI-Powered Tools', desc: 'Leverage artificial intelligence for lead generation, market analysis, property valuation, and client communications.' },
              { icon: Cloud, title: 'Cloud-Based Operations', desc: 'Work from anywhere. No physical office required. All tools, documents, and systems accessible from any device.' },
              { icon: TrendingUp, title: 'Up to 90% Commission — No Fees', desc: 'Keep up to 90% of your hard-earned commissions. No desk fees, no monthly fees, no technology charges. Simple flat split for qualified agents.' },
              { icon: Shield, title: 'Full Compliance Support', desc: 'Stay compliant with state regulations. We handle E&O insurance, legal oversight, and regulatory requirements.' },
              { icon: Zap, title: 'Marketing Campaigns', desc: 'Opt into curated marketing packages — Google Ads, social media, LinkedIn Ads — all managed for you to attract buyers, sellers, and deal flow.' },
              { icon: Globe, title: 'Statewide Coverage', desc: 'Licensed operations statewide in New York, Pennsylvania, and Florida. Serve residential and commercial clients across entire states.' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="bento-card p-8 rounded-xl"
              >
                <div className="w-10 h-10 bg-[#EFF3FF] rounded-xl flex items-center justify-center mb-5">
                  <item.icon size={18} className="text-[#002FA7]" />
                </div>
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium mb-2 text-slate-800">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section id="locations" className="py-24 md:py-32 border-t border-slate-200 bg-[#F4F6F8]" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">
              Our Markets
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-16 text-slate-900">
              Three States. Unlimited Opportunity.
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'New York', code: 'NY', img: 'https://images.unsplash.com/photo-1651608034107-12b95f5b2088?w=800&q=80&auto=format&fit=crop', desc: 'Statewide coverage — from Manhattan office towers and multifamily to upstate industrial and retail properties.' },
              { name: 'Florida', code: 'FL', img: 'https://images.unsplash.com/photo-1670861643063-0892c6fab1ce?w=800&q=80&auto=format&fit=crop', desc: 'Statewide coverage — from South Florida multifamily and retail to Central and North Florida warehouse, self storage, and office.' },
              { name: 'Pennsylvania', code: 'PA', img: 'https://images.unsplash.com/photo-1569761316261-9a8696fa2ca3?w=800&q=80&auto=format&fit=crop', desc: 'Statewide coverage — from Philadelphia office and retail markets to Lehigh Valley industrial, warehouse, and self storage.' },
            ].map((loc, i) => (
              <motion.div
                key={loc.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="location-card bg-white border border-slate-200 rounded-xl overflow-hidden group shadow-sm"
                data-testid={`location-card-${loc.code.toLowerCase()}`}
              >
                <div className="h-52 overflow-hidden">
                  <img src={loc.img} alt={loc.name} className="location-img w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={14} className="text-[#002FA7]" />
                    <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold">{loc.code}</span>
                  </div>
                  <h3 className="text-xl font-['Cabinet_Grotesk'] font-medium mb-2 text-slate-800">{loc.name}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{loc.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETING CAMPAIGNS */}
      <section id="campaigns" className="py-24 md:py-32 border-t border-slate-200" data-testid="campaigns-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">
              Marketing
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
              Grow Your Business
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-xl mb-16">
              As a cloud-based real estate brokerage, we help agents develop a powerful personal brand and provide broker-assisted marketing campaigns designed specifically for the promotion of your listings. Amplify your reach — no pressure, no mandatory spend.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: 'Social Media + Google Ads', cat: 'Digital', features: ['Google Ads', 'Facebook Ads', 'Instagram Ads', 'LinkedIn Ads', 'Monthly analytics'] },
            ].map((camp, i) => (
              <motion.div
                key={camp.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="bento-card p-8 rounded-xl flex flex-col"
              >
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#002FA7] font-semibold mb-4">{camp.cat}</div>
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium mb-3 text-slate-800">{camp.name}</h3>
                <ul className="space-y-2 mb-6 flex-1">
                  {camp.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle2 size={14} className="text-slate-300 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-slate-400 italic mb-4">Pricing available in the Agent Portal</div>
                <Link
                  to="/apply"
                  className="text-sm text-center border border-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Apply Now
                </Link>
              </motion.div>
            ))}

            {/* Private Investor Mailing List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="bento-card p-8 rounded-xl flex flex-col border-2 border-[#002FA7]/10 bg-gradient-to-br from-white to-[#F0F4FF]"
              data-testid="private-mailing-list"
            >
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#002FA7] font-semibold mb-4">Exclusive Access</div>
              <div className="w-12 h-12 bg-[#002FA7] rounded-xl flex items-center justify-center mb-4">
                <Users2 size={22} className="text-white" />
              </div>
              <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium mb-3 text-slate-800">Private Investor Mailing List</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5">
                Gain access to our proprietary private mailing list of high net worth investors, family offices, and real estate syndications — a curated network of qualified buyers actively seeking investment opportunities.
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                {['High net worth individual investors', 'Family offices & private equity', 'Real estate syndication groups', 'Qualified & vetted buyer network'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 size={14} className="text-[#002FA7] shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="text-xs text-slate-400 italic mb-4">Available exclusively to Krieger agents</div>
              <Link
                to="/apply"
                className="text-sm text-center bg-[#002FA7] text-white px-4 py-2 rounded-lg hover:bg-[#0040D6] transition-colors"
              >
                Apply Now
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LIVE STATS COUNTER */}
      {liveStats && (liveStats.active_agents > 0 || liveStats.closed_deals > 0) && (
        <section className="py-16 border-t border-slate-200 bg-[#0F1729]" data-testid="live-stats-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: liveStats.active_agents, label: 'Active Agents' },
                { value: liveStats.total_applications, label: 'Applications' },
                { value: liveStats.closed_deals, label: 'Deals Closed' },
                { value: liveStats.total_volume > 0 ? `$${(liveStats.total_volume / 1000000).toFixed(1)}M` : '$0', label: 'Total Volume' },
              ].map(s => (
                <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                  <div className="text-3xl md:text-4xl font-['Cabinet_Grotesk'] font-bold text-white">{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BROKERAGE COMPARISON CALCULATOR */}
      <section className="py-24 md:py-32 border-t border-slate-200" data-testid="comparison-calculator-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">Compare & Save</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
              How Much More Would You Earn at Krieger?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-xl mb-12">
              Enter your current commission split to see exactly how much more you'd take home with Krieger's up-to-90% split.
            </motion.p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white border border-slate-200 rounded-xl p-8 max-w-2xl">
            <div className="space-y-6">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold block mb-3">Your Current Split: {currentSplit}% / {100 - currentSplit}%</label>
                <input type="range" min="50" max="85" step="5" value={currentSplit} onChange={e => setCurrentSplit(Number(e.target.value))} className="w-full" data-testid="comparison-split-slider" />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>50/50</span><span>85/15</span></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                {[
                  { label: 'On a $500K sale (3%)', current: 500000 * 0.03 * (currentSplit / 100), krieger: 500000 * 0.03 * 0.9 },
                  { label: 'On a $2M sale (3%)', current: 2000000 * 0.03 * (currentSplit / 100), krieger: 2000000 * 0.03 * 0.9 },
                  { label: 'On a $10M sale (2%)', current: 10000000 * 0.02 * (currentSplit / 100), krieger: 10000000 * 0.02 * 0.9 },
                ].map(example => (
                  <div key={example.label} className="bg-slate-50 rounded-lg p-4">
                    <div className="text-xs text-slate-500 mb-3">{example.label}</div>
                    <div className="text-xs text-slate-400 mb-1">Your current brokerage:</div>
                    <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-400 line-through">{formatCurrency(example.current)}</div>
                    <div className="text-xs text-[#002FA7] mt-2 mb-1">With Krieger (up to 90%):</div>
                    <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{formatCurrency(example.krieger)}</div>
                    <div className="mt-2 text-sm font-bold text-emerald-600">+{formatCurrency(example.krieger - example.current)} more</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 md:py-32 border-t border-slate-200 bg-[#F4F6F8]" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">Agent Success Stories</motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-16 text-slate-900">
                Hear From Our Agents
              </motion.h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-white border border-slate-200 rounded-xl p-7 flex flex-col" data-testid={`testimonial-${i}`}
                >
                  <Quote size={24} className="text-[#002FA7]/20 mb-4" />
                  <p className="text-sm text-slate-600 leading-relaxed flex-1 mb-6">"{t.quote}"</p>
                  <div className="flex items-center gap-3 border-t border-slate-100 pt-4">
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#EFF3FF] flex items-center justify-center text-sm font-bold text-[#002FA7]">{t.name.charAt(0)}</div>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                      <div className="text-xs text-slate-400">{t.title}{t.location ? ` \u2022 ${t.location}` : ''}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AGENT LEADERBOARD */}
      {leaderboard.length > 0 && (
        <section className="py-24 md:py-32 border-t border-slate-200" data-testid="leaderboard-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">Top Performers</motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
                Our Agents Are Closing Deals
              </motion.h2>
              <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-xl mb-12">
                See what top-producing agents at Krieger are accomplishing. These are real results from real agents.
              </motion.p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {leaderboard.slice(0, 6).map((agent, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 hover:shadow-md hover:border-[#002FA7]/20 transition-all"
                  data-testid={`leaderboard-agent-${i}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                    ${i === 0 ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' :
                      i === 1 ? 'bg-slate-100 text-slate-600 ring-2 ring-slate-300' :
                      i === 2 ? 'bg-orange-50 text-orange-600 ring-2 ring-orange-200' :
                      'bg-[#EFF3FF] text-[#002FA7]'}`}>
                    {i < 3 ? ['1st', '2nd', '3rd'][i] : `#${i + 1}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{agent.display_name}</div>
                    <div className="text-xs text-slate-400">{agent.location || 'Agent'} &middot; {agent.deals} deal{agent.deals !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{formatCurrency(agent.volume)}</div>
                    <div className="text-[10px] text-slate-400">total volume</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECRUIT LEADERBOARD */}
      {recruitLeaderboard.length > 0 && (
        <section className="py-24 md:py-32 border-t border-slate-200 bg-gradient-to-b from-[#F8FAFF] to-white" data-testid="recruit-leaderboard-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.span variants={fadeUp} className="text-[10px] tracking-[0.3em] uppercase text-[#002FA7] font-semibold block mb-4">Revenue Share Network</motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-4 text-slate-900">
                Top Recruiters Building Teams
              </motion.h2>
              <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-xl mb-12">
                Our eXp-style revenue share model rewards agents who build teams. Earn overrides up to 3 levels deep when your recruits close deals.
              </motion.p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recruitLeaderboard.slice(0, 6).map((recruiter, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-[#002FA7]/20 transition-all"
                  data-testid={`recruit-leaderboard-${i}`}
                >
                  <div className="flex items-center gap-4 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                      ${i === 0 ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' :
                        i === 1 ? 'bg-slate-100 text-slate-600 ring-2 ring-slate-300' :
                        i === 2 ? 'bg-orange-50 text-orange-600 ring-2 ring-orange-200' :
                        'bg-[#EFF3FF] text-[#002FA7]'}`}>
                      {i < 3 ? ['1st', '2nd', '3rd'][i] : `#${i + 1}`}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800">{recruiter.display_name}</div>
                      <div className="text-xs text-slate-400">{recruiter.location || 'Agent'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                    <div className="text-center">
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{recruiter.direct_recruits}</div>
                      <div className="text-[9px] text-slate-400">Direct</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-800">{recruiter.network_size}</div>
                      <div className="text-[9px] text-slate-400">Network</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-emerald-600">{formatCurrency(recruiter.total_override_earned)}</div>
                      <div className="text-[9px] text-slate-400">Earned</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-slate-200 bg-[#F4F6F8]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-['Cabinet_Grotesk'] font-bold tracking-tight mb-6 text-slate-900">
              Ready to Keep Up to 90%<br />of What You Earn?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-md mx-auto mb-10">
              High-performing residential and commercial agents across New York, Pennsylvania, and Florida are switching to Krieger. No monthly fees. No caps. Qualified agents earn up to 90%. Apply in minutes.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <Link
                to="/apply"
                className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-8 py-3 rounded-full font-medium text-sm hover:bg-[#0040D6] transition-colors shadow-lg shadow-blue-900/20"
                data-testid="cta-apply-btn"
              >
                Start Your Application <ArrowRight size={16} />
              </Link>
              <button
                onClick={login}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-8 py-3 rounded-full font-medium text-sm hover:bg-slate-50 transition-colors shadow-sm"
                data-testid="cta-login-btn"
              >
                Sign In with Google
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 py-12 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <span className="font-['Cabinet_Grotesk'] text-lg font-bold tracking-[0.15em] text-slate-900">KRIEGER</span>
              <p className="text-xs text-slate-400 mt-1">Real Estate Brokerage — Residential & Commercial</p>
            </div>
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="#commission" className="hover:text-slate-700 transition-colors">Commission</a>
              <Link to="/commission-plans" className="hover:text-slate-700 transition-colors">Commission Plans</Link>
              <Link to="/why-join-krieger" className="hover:text-slate-700 transition-colors">Why Krieger</Link>
              <Link to="/agent-income-calculator" className="hover:text-slate-700 transition-colors">Calculator</Link>
              <Link to="/apply" className="hover:text-slate-700 transition-colors">Apply</Link>
            </div>
          </div>
          <div className="border-t border-slate-100 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} Krieger Real Estate. All rights reserved.</p>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <MapPin size={12} /> New York &middot; Pennsylvania &middot; Florida
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
